/**
 * Feature Configuration Utility Functions
 * 
 * Provides utilities for loading, parsing, and validating feature configurations
 * from JSON files.
 */

import { FeatureConfig, Feature } from '../types/featureConfig';

/**
 * Validates that an object conforms to the Feature interface
 * @param feature - The object to validate
 * @returns true if valid, false otherwise
 */
function isValidFeature(feature: unknown): feature is Feature {
  if (typeof feature !== 'object' || feature === null) {
    return false;
  }

  const f = feature as Record<string, unknown>;

  // Check required fields
  if (
    typeof f.id !== 'string' ||
    typeof f.title !== 'string' ||
    typeof f.description !== 'string'
  ) {
    return false;
  }

  // Check optional fields if present
  if (f.category !== undefined && typeof f.category !== 'string') {
    return false;
  }
  if (f.icon !== undefined && typeof f.icon !== 'string') {
    return false;
  }
  if (f.link !== undefined && typeof f.link !== 'string') {
    return false;
  }

  return true;
}

/**
 * Validates that an object conforms to the FeatureConfig interface
 * @param config - The object to validate
 * @returns true if valid, false otherwise
 */
function isValidFeatureConfig(config: unknown): config is FeatureConfig {
  if (typeof config !== 'object' || config === null) {
    return false;
  }

  const c = config as Record<string, unknown>;

  // Check required fields
  if (
    typeof c.version !== 'string' ||
    typeof c.releaseDate !== 'string' ||
    typeof c.title !== 'string'
  ) {
    return false;
  }

  // Check features array
  if (!Array.isArray(c.features)) {
    return false;
  }

  // Validate each feature in the array
  return c.features.every((feature) => isValidFeature(feature));
}

/**
 * Parses JSON data and validates it against the FeatureConfig interface
 * @param jsonData - JSON string or parsed object to validate
 * @returns Validated FeatureConfig object
 * @throws Error if JSON is invalid or doesn't match FeatureConfig interface
 */
export function parseFeatureConfig(
  jsonData: string | Record<string, unknown>
): FeatureConfig {
  let parsed: unknown;

  try {
    // Parse JSON if string is provided
    if (typeof jsonData === 'string') {
      parsed = JSON.parse(jsonData);
    } else {
      parsed = jsonData;
    }
  } catch (error) {
    throw new Error(
      `Failed to parse feature configuration: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }

  // Validate against FeatureConfig interface
  if (!isValidFeatureConfig(parsed)) {
    throw new Error(
      'Feature configuration does not match expected schema. ' +
        'Ensure version, releaseDate, title (strings) and features (array) are present. ' +
        'Each feature must have id, title, description (required) and optional category, icon, link (strings).'
    );
  }

  return parsed as FeatureConfig;
}

/**
 * Loads feature configuration from a JSON file (browser-based)
 * Uses fetch API to load the configuration file
 * @param filePath - Path to the features-config.json file
 * @returns Promise resolving to validated FeatureConfig object
 * @throws Error if file cannot be loaded or is invalid
 */
export async function loadFeatureConfig(filePath: string): Promise<FeatureConfig> {
  try {
    const response = await fetch(filePath);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const jsonData = await response.json();
    return parseFeatureConfig(jsonData);
  } catch (error) {
    throw new Error(
      `Failed to load feature configuration from ${filePath}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
}

/**
 * Synchronously loads feature configuration from a JSON string
 * Useful for testing or when data is already loaded
 * @param jsonString - JSON string containing feature configuration
 * @returns Validated FeatureConfig object
 * @throws Error if JSON is invalid or doesn't match FeatureConfig interface
 */
export function loadFeatureConfigFromString(jsonString: string): FeatureConfig {
  return parseFeatureConfig(jsonString);
}

/**
 * Gets a specific feature by ID from a FeatureConfig
 * @param config - The FeatureConfig object
 * @param featureId - The ID of the feature to retrieve
 * @returns The Feature object if found, undefined otherwise
 */
export function getFeatureById(
  config: FeatureConfig,
  featureId: string
): Feature | undefined {
  return config.features.find((f) => f.id === featureId);
}

/**
 * Gets all features in a specific category from a FeatureConfig
 * @param config - The FeatureConfig object
 * @param category - The category to filter by
 * @returns Array of Feature objects in the specified category
 */
export function getFeaturesByCategory(
  config: FeatureConfig,
  category: string
): Feature[] {
  return config.features.filter((f) => f.category === category);
}

/**
 * Gets all unique categories from a FeatureConfig
 * @param config - The FeatureConfig object
 * @returns Array of unique category names
 */
export function getCategories(config: FeatureConfig): string[] {
  const categories = new Set<string>();
  config.features.forEach((f) => {
    if (f.category) {
      categories.add(f.category);
    }
  });
  return Array.from(categories).sort();
}
